/**
 * Centralised chain-type detection for addresses.
 *
 * Before this helper, the codebase used the binary check
 * `!address.startsWith('0x')` to discriminate EVM from Solana — which would
 * mis-classify Tron `T...` addresses as Solana once Tron support is added.
 *
 * Use this helper at every site that needs to know which chain an address
 * belongs to. Source of truth: address shape.
 *
 * Privacy invariant: callers MUST route every chain-specific operation
 * (wallet popups, RPC calls, signature requests) through the chain returned by
 * this function. Falling back to "the first connected wallet" leaks identity
 * across chains and breaks the cross-chain privacy guarantee.
 *
 * Discrimination rules:
 *   - EVM:    "0x" prefix + length 42        (0x + 40 hex chars)
 *   - Tron:   "T"  prefix + length 34        (Base58Check of [0x41 || 20-byte addr])
 *             — Tron addresses are ALWAYS exactly 34 chars; this is a hard
 *             invariant of the Base58Check encoding of a 21-byte payload.
 *   - Solana: base58, length 32–44, NOT starting with T (variable length
 *             because Ed25519 pubkeys vary in their base58 encoding)
 */

const BASE58_RE = /^[1-9A-HJ-NP-Za-km-z]+$/;
const TRON_RE = /^T[1-9A-HJ-NP-Za-km-z]{33}$/;

/**
 * @param {string} address
 * @returns {'evm' | 'tron' | 'solana' | null}
 */
export const getChainType = (address) => {
  if (!address || typeof address !== 'string') return null;
  if (address.startsWith('0x') && address.length === 42) return 'evm';
  if (TRON_RE.test(address)) return 'tron';
  if (!address.startsWith('0x') && BASE58_RE.test(address) && address.length >= 32 && address.length <= 44) {
    return 'solana';
  }
  return null;
};

/** Convenience predicates. Prefer `getChainType(addr) === 'foo'` when calling
 *  from new code; these wrappers exist so legacy sites can migrate without
 *  changing call shape. */
export const isEvmAddress    = (addr) => getChainType(addr) === 'evm';
export const isTronAddress   = (addr) => getChainType(addr) === 'tron';
export const isSolanaAddress = (addr) => getChainType(addr) === 'solana';

// Lazy import — bs58check is available as a transitive dep, but we don't want
// to pull it in for callers that only need address-type classification.
let _bs58check = null;
const bs58check = () => {
  if (_bs58check) return _bs58check;
  // eslint-disable-next-line global-require
  _bs58check = require('bs58check');
  return _bs58check;
};

/**
 * Decode a Tron T... address into its 20-byte EVM-equivalent form. The Tron
 * address is Base58Check(0x41 || 20-byte addr); we strip the 0x41 prefix and
 * the 4-byte checksum to get the same 20 bytes the Solidity contract sees.
 *
 * @param {string} tronAddr - "T..." 34-char base58check
 * @returns {string} - 0x-prefixed 40-char hex (EVM-format address)
 */
export const tronAddressToEvmHex = (tronAddr) => {
  if (!isTronAddress(tronAddr)) {
    throw new Error(`Not a valid Tron address: ${tronAddr}`);
  }
  const decoded = bs58check().decode(tronAddr); // 21 bytes: 0x41 || 20-byte addr
  return '0x' + Buffer.from(decoded).slice(1).toString('hex');
};

/**
 * Encode an EVM 20-byte address as a Tron T... Base58Check address.
 * Used to verify that a reconstructed secp256k1 private key produces the
 * same Tron address the backend reported.
 *
 * @param {string} evmHex - 0x-prefixed 20-byte hex (EVM-format address)
 * @returns {string} - "T..." 34-char Tron Base58Check address
 */
export const evmHexToTronAddress = (evmHex) => {
  const clean = (evmHex || '').replace(/^0x/, '').toLowerCase();
  if (clean.length !== 40) {
    throw new Error(`Invalid EVM hex length: expected 40 chars, got ${clean.length}`);
  }
  const payload = Buffer.concat([Buffer.from([0x41]), Buffer.from(clean, 'hex')]);
  return bs58check().encode(payload);
};

/**
 * Build a block-explorer URL for a tx hash. Prefers backend-supplied
 * `block_explorer` from `supportedChains` and picks the correct URL shape
 * per chain family:
 *   - EVM:    {base}/tx/{hash}
 *   - Tron:   {base}/#/transaction/{hash}
 *   - Solana: {base}/tx/{hash}[?cluster=devnet]
 *
 * @param {object|string} chainOrName - chain object from supportedChains, or a chain_name string
 * @param {string} txHash
 * @param {Array} supportedChains - optional list to resolve `chainOrName` when a string is passed
 * @returns {string} URL or '' if no tx hash
 */
export const buildExplorerTxUrl = (chainOrName, txHash, supportedChains = []) => {
  if (!txHash) return '';
  let chain = null;
  if (chainOrName && typeof chainOrName === 'object') {
    chain = chainOrName;
  } else if (typeof chainOrName === 'string' && supportedChains?.length) {
    const needle = chainOrName.toLowerCase();
    chain = supportedChains.find(
      (c) =>
        (c.chain_name || c.chainName || '').toLowerCase() === needle ||
        (c.name || '').toLowerCase() === needle,
    );
  }

  const name = (chain?.chain_name || chain?.chainName || chainOrName || '').toString().toLowerCase();
  const chainType = (chain?.chain_type || chain?.chainType || '').toLowerCase();
  const rawBase = chain?.block_explorer || chain?.blockExplorer || '';
  const base = rawBase.replace(/\/+$/, '');

  // Tron — uses /#/transaction/{hash}. Defaults to Nile when no base supplied.
  if (chainType === 'tron' || name.startsWith('tron')) {
    const tronBase = base || (name.includes('mainnet') || name === 'tron' ? 'https://tronscan.org' : 'https://nile.tronscan.org');
    return `${tronBase}/#/transaction/${txHash}`;
  }

  // Solana — devnet needs ?cluster=devnet.
  if (chainType === 'solana' || name.startsWith('solana')) {
    const solBase = base || 'https://explorer.solana.com';
    const isDevnet = name.includes('devnet') || name.includes('testnet');
    return isDevnet ? `${solBase}/tx/${txHash}?cluster=devnet` : `${solBase}/tx/${txHash}`;
  }

  // EVM (default)
  const evmBase = base || 'https://etherscan.io';
  return `${evmBase}/tx/${txHash}`;
};
